'use client';

import { useMemo, useState } from 'react';
import { buildEmailSignatureHtml, type EmailSignatureValues } from '@/lib/browser-widget-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserEmailSignatureGenerator() {
  const [values, setValues] = useState<EmailSignatureValues>({
    name: 'Jordan Rivera',
    title: 'Product Marketing Lead',
    company: 'Cleanor Labs',
    phone: '+1 (555) 010-2030',
    email: 'jordan@cleanor.app',
    website: 'cleanor.app',
    logo: '',
    accent: '#2563eb',
  });
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const html = useMemo(() => buildEmailSignatureHtml(values), [values]);

  function setValue(key: keyof EmailSignatureValues, value: string) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCopyHtml() {
    try {
      await copyText(html);
      setCopyMessage('Signature HTML copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Email</span>
        <h2 className="tool-live-title">Email signature generator</h2>
        <p className="tool-live-subtitle">
          Build a table-based, inline-styled signature that survives Gmail, Outlook, and Apple Mail.
          Copy the HTML and paste it into your mail client.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Full name</span>
              <input
                onChange={(e) => setValue('name', e.target.value)}
                type="text"
                value={values.name}
              />
            </label>
            <label className="tool-live-field">
              <span>Job title</span>
              <input
                onChange={(e) => setValue('title', e.target.value)}
                type="text"
                value={values.title}
              />
            </label>
            <label className="tool-live-field">
              <span>Company</span>
              <input
                onChange={(e) => setValue('company', e.target.value)}
                type="text"
                value={values.company}
              />
            </label>
            <label className="tool-live-field">
              <span>Phone</span>
              <input
                onChange={(e) => setValue('phone', e.target.value)}
                type="text"
                value={values.phone}
              />
            </label>
            <label className="tool-live-field">
              <span>Email</span>
              <input
                onChange={(e) => setValue('email', e.target.value)}
                type="text"
                value={values.email}
              />
            </label>
            <label className="tool-live-field">
              <span>Website</span>
              <input
                onChange={(e) => setValue('website', e.target.value)}
                placeholder="cleanor.app"
                type="text"
                value={values.website}
              />
            </label>
            <label className="tool-live-field">
              <span>Accent color</span>
              <input
                onChange={(e) => setValue('accent', e.target.value)}
                placeholder="#2563eb"
                type="text"
                value={values.accent}
              />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Logo image URL (optional)</span>
              <input
                onChange={(e) => setValue('logo', e.target.value)}
                placeholder="https://example.com/logo.png"
                type="text"
                value={values.logo}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopyHtml} type="button">
              Copy HTML
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <span
            style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}
          >
            Live preview
          </span>
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e5e7eb',
              overflowX: 'auto',
            }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
          <label className="tool-live-field tool-live-field-full" style={{ marginTop: '16px' }}>
            <span>Signature HTML</span>
            <textarea readOnly rows={10} value={html} />
          </label>
          <p className="tool-live-panel-note">
            Generated locally in your browser. Nothing is uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
